import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { ArrowLeft, Send, Heart, Clock, AlertTriangle, FileText, Plus, CreditCard, Loader2, X, Pause, Play, CalendarPlus, Sparkles, Paperclip, CornerUpLeft, ArrowDown } from 'lucide-react';
import ChatSummary from '../components/ChatSummary';
import VeyonikaSessionSummary from '../components/VeyonikaSessionSummary';
import SessionExtension from '../components/SessionExtension';
import InSessionRequests from '../components/InSessionRequests';
import InSessionBooking from '../components/InSessionBooking';
import SessionResources from '../components/SessionResources';
import AccountConversionModal from '../components/AccountConversionModal';

// Duration mapping for each grade (in minutes) — aligned with simplified care grades
const GRADE_DURATIONS = {
  'G0': 15,
  'G1': 30,
  'G3': 30,
  'G4': 40,
  'G5': 60
};

// (Legacy extension constants removed — pricing now lives behind /api/pricing/* and is rendered by <SessionExtension />.)

export default function ChatInterface() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [session, setSession] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showVeyonikaSummary, setShowVeyonikaSummary] = useState(false);
  const [showExtensionPrompt, setShowExtensionPrompt] = useState(false);
  const [showBookingPrompt, setShowBookingPrompt] = useState(false);
  const [extensionPromptShown, setExtensionPromptShown] = useState(false);
  const [extendedMinutes, setExtendedMinutes] = useState(0);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [endedByOther, setEndedByOther] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [otherTyping, setOtherTyping] = useState(false);
  const [showJump, setShowJump] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [guestStatus, setGuestStatus] = useState(null);
  const [showConversion, setShowConversion] = useState(false);
  const [guestExpired, setGuestExpired] = useState(false);
  const guestNoticeShownRef = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const typingHeartbeatRef = useRef(0);
  const lastMsgKeyRef = useRef(null);
  const pollInterval = useRef(null);
  const timerInterval = useRef(null);
  const endTimeRef = useRef(null);
  const pausedTimeRef = useRef(null);
  // Use ref to always have current user value in callbacks
  const userRef = useRef(user);
  // Use ref for timerStarted to avoid stale closure in polling
  const timerStartedRef = useRef(false);
  // Track if we already handled session end
  const sessionEndHandledRef = useRef(false);

  // Keep userRef in sync with user
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  // Keep timerStartedRef in sync with timerStarted
  useEffect(() => {
    timerStartedRef.current = timerStarted;
  }, [timerStarted]);

  // Auth check effect - redirect if not authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to continue');
      navigate('/auth');
      return;
    }
    // Give time for App.js to load user data
    const checkAuth = setTimeout(() => {
      setAuthChecked(true);
    }, 100);
    return () => clearTimeout(checkAuth);
  }, [navigate]);

  // Redirect if auth check complete but no user
  useEffect(() => {
    if (authChecked && !user) {
      toast.error('Session expired. Please login again.');
      navigate('/auth');
    }
  }, [authChecked, user, navigate]);

  // Fetch messages - defined first since used by useEffect
  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages/${sessionId}`);
      setMessages([...response.data]);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Fetch session details
  const fetchSession = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(response.data);
      // Check for existing extensions (canonical field: extension_minutes)
      if (response.data.extension_minutes) {
        setExtendedMinutes(response.data.extension_minutes);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch session:', error);
      return null;
    }
  }, [sessionId]);

  // Update session status
  const updateSessionStatus = useCallback(async (status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sessions/${sessionId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to update session status');
    }
  }, [sessionId]);

  // Start countdown timer
  const startTimer = useCallback((sessionData) => {
    if (timerStarted || !sessionData) return;
    
    const gradeCode = sessionData.grade_code || 'G1';
    const baseDurationMinutes = GRADE_DURATIONS[gradeCode] || 15;
    const totalExtendedMinutes = sessionData.extension_minutes || 0;
    const totalDurationMinutes = baseDurationMinutes + totalExtendedMinutes;
    const durationSeconds = totalDurationMinutes * 60;
    
    // Use started_at if available, otherwise use current time
    const startTime = sessionData.started_at 
      ? new Date(sessionData.started_at).getTime()
      : Date.now();
    
    const endTime = startTime + (durationSeconds * 1000);
    endTimeRef.current = endTime;
    
    setTimerStarted(true);
    setExtendedMinutes(totalExtendedMinutes);
    
    // Track if extension prompt was shown in this timer instance
    let promptShownInTimer = false;
    
    // Update timer every second
    timerInterval.current = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((endTimeRef.current - now) / 1000));
      setTimeRemaining(remaining);
      
      // Use userRef to get current user value
      const currentUser = userRef.current;
      
      // Show extension prompt at 5 minutes (only for care seekers, only once)
      if (remaining === 300 && currentUser?.role === 'care_seeker' && !promptShownInTimer) {
        setShowExtensionPrompt(true);
        setExtensionPromptShown(true);
        promptShownInTimer = true;
        toast.warning('5 minutes remaining in this session');
      }
      
      // Warning at 2 minutes
      if (remaining === 120) {
        toast.warning('2 minutes remaining!', { duration: 5000 });
      }
      
      // Warning at 30 seconds
      if (remaining === 30) {
        toast.warning('30 seconds remaining!', { duration: 5000 });
      }
      
      // Time's up - auto-end session
      if (remaining === 0) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
        toast.error('Session time has ended');
        
        // Auto-end session and show summary for provider
        const currentUser = userRef.current;
        if (currentUser && !sessionEndHandledRef.current) {
          sessionEndHandledRef.current = true;
          setSessionEnded(true);
          
          // Update session status to completed
          const token = localStorage.getItem('token');
          axios.put(`${API}/sessions/${sessionId}/status?status=completed`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => console.error('Failed to auto-end session:', err));
          
          if (currentUser.role === 'care_provider') {
            setShowSummary(true);
          } else {
            setTimeout(() => {
              navigate('/seeker-dashboard');
            }, 2000);
          }
        }
      }
    }, 1000);
  }, [timerStarted, sessionId, navigate]);

  // Pause timer
  const handlePauseTimer = useCallback(() => {
    if (!timerStarted || timerPaused) return;
    
    // Store remaining time when paused
    pausedTimeRef.current = timeRemaining;
    
    // Clear the interval
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
    
    setTimerPaused(true);
    toast.info('Session paused');
  }, [timerStarted, timerPaused, timeRemaining]);

  // Resume timer
  const handleResumeTimer = useCallback(() => {
    if (!timerStarted || !timerPaused) return;
    
    // Calculate new end time based on paused remaining time
    const newEndTime = Date.now() + (pausedTimeRef.current * 1000);
    endTimeRef.current = newEndTime;
    
    // Restart the interval
    timerInterval.current = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((endTimeRef.current - now) / 1000));
      setTimeRemaining(remaining);
      
      const currentUser = userRef.current;
      
      if (remaining === 300 && currentUser?.role === 'care_seeker' && !extensionPromptShown) {
        setShowExtensionPrompt(true);
        setExtensionPromptShown(true);
        toast.warning('5 minutes remaining in this session');
      }
      
      if (remaining === 120) {
        toast.warning('2 minutes remaining!', { duration: 5000 });
      }
      
      if (remaining === 30) {
        toast.warning('30 seconds remaining!', { duration: 5000 });
      }
      
      if (remaining === 0) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
        toast.error('Session time has ended');
        
        // Auto-end session and show summary for provider
        if (currentUser && !sessionEndHandledRef.current) {
          sessionEndHandledRef.current = true;
          setSessionEnded(true);
          
          const token = localStorage.getItem('token');
          axios.put(`${API}/sessions/${sessionId}/status?status=completed`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => console.error('Failed to auto-end session:', err));
          
          if (currentUser.role === 'care_provider') {
            setShowSummary(true);
          } else {
            setTimeout(() => {
              navigate('/seeker-dashboard');
            }, 2000);
          }
        }
      }
    }, 1000);
    
    setTimerPaused(false);
    toast.success('Session resumed');
  }, [timerStarted, timerPaused, extensionPromptShown, sessionId, navigate]);

  // (Legacy handleExtendSession removed during A1-A11 cleanup —
  // extension UI is now driven by the <SessionExtension /> component
  // which uses /api/pricing/extension/* end-to-end.)

  // Main useEffect for initialization
  useEffect(() => {
    // Don't run if user is not loaded yet or auth not checked
    if (!user || !authChecked) return;
    
    const initChat = async () => {
      await fetchMessages();
      const sessionData = await fetchSession();
      
      // Use userRef for current user value
      const currentUser = userRef.current;
      if (!currentUser) return;
      
      // Provider sets session to active when they join
      if (currentUser.role === 'care_provider') {
        await updateSessionStatus('active');
        // Refresh session to get started_at
        const updatedSession = await fetchSession();
        if (updatedSession?.status === 'active') {
          startTimer(updatedSession);
        }
      } else if (sessionData?.status === 'active' && sessionData?.started_at) {
        // Seeker joins an active session - start timer immediately
        startTimer(sessionData);
      }
    };
    
    initChat();

    // Use polling for messages and session status
    pollInterval.current = setInterval(async () => {
      await fetchMessages();

      // Lightweight typing-indicator poll (ephemeral, never stored as a message)
      try {
        const token = localStorage.getItem('token');
        const tp = await axios.get(`${API}/sessions/${sessionId}/typing`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOtherTyping(!!tp.data?.typing);
      } catch (_) { /* non-blocking */ }
      
      // Use userRef for current user value
      const currentUser = userRef.current;
      if (!currentUser) return;
      
      // Check session status for changes (ended by other party)
      const sessionData = await fetchSession();
      
      // Detect if session was ended by the other party
      if (sessionData?.status === 'completed' && !sessionEndHandledRef.current) {
        sessionEndHandledRef.current = true;
        setSessionEnded(true);
        setEndedByOther(true);
        
        // Stop the timer
        if (timerInterval.current) {
          clearInterval(timerInterval.current);
          timerInterval.current = null;
        }
        
        // Show appropriate notification and action
        if (currentUser.role === 'care_provider') {
          toast.info('Session ended by care seeker. Please complete the session summary.');
          setShowSummary(true);
        } else {
          toast.info('Session ended by care provider.');
          // Give user a moment to see the message before redirecting
          setTimeout(() => {
            navigate('/seeker-dashboard');
          }, 2000);
        }
        return;
      }
      
      // For care seekers: Check if session became active (provider joined)
      // Use timerStartedRef to get the current value, not the closure captured value
      if (currentUser.role === 'care_seeker' && !timerStartedRef.current) {
        if (sessionData?.status === 'active' && sessionData?.started_at) {
          console.log('Session is now active, starting timer for seeker');
          startTimer(sessionData);
        }
      }
    }, 2000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
      // Don't clear timerInterval here - it should persist across re-renders
      // Timer cleanup happens only when component unmounts (sessionId changes)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, user, authChecked, fetchMessages, fetchSession, updateSessionStatus, startTimer]);

  // Cleanup timer interval when sessionId changes (navigating to different session)
  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
    };
  }, [sessionId]);

  // Smart scrolling: only auto-scroll when the user is already at the bottom
  // or when they just sent a message. Otherwise surface a "Jump to latest" pill.
  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    isAtBottomRef.current = true;
    setShowJump(false);
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    isAtBottomRef.current = atBottom;
    if (atBottom) setShowJump(false);
  }, []);

  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    const key = `${messages.length}:${last.id}`;
    // Ignore poll refreshes that don't actually add a message (prevents forced scroll).
    if (key === lastMsgKeyRef.current) return;
    const isFirstRender = lastMsgKeyRef.current === null;
    lastMsgKeyRef.current = key;
    if (isFirstRender) {
      scrollToBottom(false);
      return;
    }
    const mine = last && (last.sender_id === user?.id);
    if (isAtBottomRef.current || mine) {
      scrollToBottom(true);
    } else {
      setShowJump(true);
    }
  }, [messages, user, scrollToBottom]);

  // Send a lightweight typing heartbeat (throttled to ~1.5s), not persisted as a message.
  const pingTyping = useCallback((typing) => {
    const now = Date.now();
    if (typing && now - typingHeartbeatRef.current < 1500) return;
    typingHeartbeatRef.current = now;
    const token = localStorage.getItem('token');
    axios.post(`${API}/sessions/${sessionId}/typing?is_typing=${typing}`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [sessionId]);

  // Phase 4: guest free-chat status (server-authoritative). Poll for the 10-min
  // notice, the 12-min hard limit, and session expiry. Runs only for guest users.
  useEffect(() => {
    if (user?.role !== 'guest') return;
    let cancelled = false;
    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/guest/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setGuestStatus(res.data);
        if (res.data.notice && !guestNoticeShownRef.current && !res.data.free_over) {
          guestNoticeShownRef.current = true;
          toast('Your introductory chat is nearly complete. Create an account to continue your conversation securely.', { duration: 8000 });
        }
        if (res.data.free_over) setShowConversion(true);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 401 && e.response?.data?.detail === 'guest_expired') {
          setGuestExpired(true);
        }
      }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, sessionId]);

  const isGuest = user?.role === 'guest';
  const guestLocked = isGuest && (guestExpired || (guestStatus && guestStatus.free_over));

  // If the guest session already expired (detected on hard reload), show the overlay.
  useEffect(() => {
    if (user?.expired) setGuestExpired(true);
  }, [user]);

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on remaining time
  const getTimerColor = () => {
    if (timeRemaining === null) return 'text-[#718096]';
    if (timeRemaining <= 30) return 'text-red-500 animate-pulse';
    if (timeRemaining <= 120) return 'text-orange-500';
    return 'text-emerald-600';
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages`, {
        session_id: sessionId,
        content: newMessage,
        reply_to: replyTo?.id || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      setReplyTo(null);
      pingTyping(false);
      isAtBottomRef.current = true;
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const endSession = async () => {
    try {
      await updateSessionStatus('completed');
      sessionEndHandledRef.current = true;
      setSessionEnded(true);
      
      // Stop the timer
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
      
      toast.success('Session ended');
      
      // For providers, show option to generate summary
      if (user.role === 'care_provider') {
        setShowSummary(true);
      } else {
        navigate('/seeker-dashboard');
      }
    } catch (error) {
      toast.error('Failed to end session');
    }
  };

  const handleSummaryClose = () => {
    setShowSummary(false);
    navigate('/provider-dashboard');
  };

  if (loading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#718096]">Loading chat...</p>
        </div>
      </div>
    );
  }

  // Wait for user context to be loaded - redirect handled by useEffect
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#718096]">Verifying session...</p>
        </div>
      </div>
    );
  }

  const isProvider = user.role === 'care_provider';
  const roleCode = isProvider ? 'CG' : 'CS';
  const roleLabel = isProvider ? 'Care Giver' : 'Care Seeker';
  const msgById = messages.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});

  return (
    <div className="h-screen bg-[#FAF9F6] flex flex-col">
      {/* Role Indicator Banner */}
      <div className="text-white py-2 px-4 text-center sticky top-0 z-[60]" style={{ backgroundColor: isProvider ? '#2563eb' : '#059669' }}>
        <div className="container mx-auto flex items-center justify-center gap-3">
          <span className="bg-white font-bold px-3 py-1 rounded-full text-sm" style={{ color: isProvider ? '#2563eb' : '#059669' }}>{roleCode}</span>
          <span className="font-medium">{roleLabel}</span>
          <span className="opacity-60 text-sm">|</span>
          <span className="opacity-80 text-sm">{isProvider ? `Dr. ${user.name}` : user.name}</span>
          <span className="opacity-60 text-sm">|</span>
          <span className="opacity-80 text-sm">Session: {sessionId?.slice(0, 8)}...</span>
          {extendedMinutes > 0 && (
            <>
              <span className="opacity-60 text-sm">|</span>
              <span className="bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-medium">
                +{extendedMinutes} min extended
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Header - Simplified */}
      <header className="glass-effect border-b border-[#E2E8F0] sticky top-[40px] z-50 bg-white">
        <div className="container mx-auto px-6 py-3 flex items-center">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate(user.role === 'care_seeker' ? '/seeker-dashboard' : '/provider-dashboard')}
              className="text-[#718096] hover:text-primary transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center gap-2">
              <Heart className="text-primary" size={24} />
              <div className="flex flex-col leading-tight">
                <h1 className="text-lg font-heading font-semibold">
                  {session?.grade_code ? `${session.grade_code} Chat` : 'Anonymous Chat'}
                </h1>
                {user?.role === 'care_provider' && session?.presenting_concern && (
                  <p
                    className="text-xs text-[#A0AEC0] italic line-clamp-1 max-w-[60ch]"
                    title={session.presenting_concern}
                    data-testid="header-presenting-concern"
                  >
                    What brought them here:{' '}
                    {session.presenting_concern.length > 100
                      ? `${session.presenting_concern.slice(0, 100)}…`
                      : session.presenting_concern}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Messages Area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 relative" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
        <div className="container mx-auto max-w-4xl space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-[#718096] py-12">
              <p className="text-lg mb-2">No messages yet</p>
              <p className="text-sm">Start the conversation...</p>
            </div>
          ) : (
            messages.map((message) => {
              if (message.sender_role === 'system') {
                return (
                  <div key={message.id} data-testid={`message-${message.id}`} className="flex justify-center">
                    <div className="max-w-[85%] text-center text-xs sm:text-sm text-[#7A625C] bg-[#FDF2F0] border border-[#FAC7C0] rounded-2xl px-4 py-2" data-testid="system-message">
                      {message.content}
                    </div>
                  </div>
                );
              }
              const isCurrentUser = message.sender_id === user.id;
              const quoted = message.reply_to ? msgById[message.reply_to] : null;
              const ReplyBtn = (
                <button
                  type="button"
                  aria-label="Reply"
                  data-testid={`reply-btn-${message.id}`}
                  onClick={() => setReplyTo(message)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-[#A0AEC0] hover:text-primary p-1 self-center"
                >
                  <CornerUpLeft size={16} />
                </button>
              );
              return (
                <div
                  key={message.id}
                  data-testid={`message-${message.id}`}
                  className={`group flex items-end gap-1 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  {isCurrentUser && !sessionEnded && ReplyBtn}
                  <div className={isCurrentUser ? 'chat-bubble-user' : 'chat-bubble-system'}>
                    {quoted && (
                      <div className="mb-1 border-l-2 border-current/40 pl-2 text-xs opacity-80 line-clamp-2" data-testid={`quoted-${message.id}`}>
                        {quoted.sender_role === 'system' ? 'Automated message' : (quoted.content || '')}
                      </div>
                    )}
                    <p className="text-sm leading-relaxed">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {new Date(message.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  {!isCurrentUser && !sessionEnded && ReplyBtn}
                </div>
              );
            })
          )}
          {otherTyping && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="chat-bubble-system flex items-center gap-1 py-3">
                <span className="w-2 h-2 bg-[#7A625C] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-[#7A625C] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-[#7A625C] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showJump && (
          <button
            type="button"
            data-testid="jump-to-latest-btn"
            onClick={() => scrollToBottom(true)}
            className="sticky bottom-2 float-right mr-2 z-30 flex items-center gap-1 px-4 py-2 rounded-full bg-[#7D202B] text-white text-sm shadow-lg hover:bg-[#963A42] transition-colors"
          >
            <ArrowDown size={16} /> New messages
          </button>
        )}
      </div>

      {/* Bottom Control Bar - Timer, Pause, End Session */}
      <div className="border-t border-[#E2E8F0] bg-white sticky bottom-0 z-50">
        {/* Timer and Controls Row */}
        <div className="container mx-auto max-w-4xl px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Timer Display */}
            <div 
              data-testid="session-timer"
              className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border ${
                timeRemaining !== null && timeRemaining <= 120 ? 'border-orange-300 bg-orange-50' : 'border-[#E2E8F0]'
              } ${timerPaused ? 'opacity-60' : ''}`}
            >
              {timeRemaining !== null && timeRemaining <= 30 ? (
                <AlertTriangle size={18} className="text-red-500" />
              ) : (
                <Clock size={18} className={getTimerColor()} />
              )}
              <span className={`font-mono font-semibold text-lg ${getTimerColor()}`}>
                {formatTime(timeRemaining)}
              </span>
              {!timerStarted && (
                <span className="text-xs text-[#718096]">Waiting...</span>
              )}
              {timerPaused && (
                <span className="text-xs text-orange-600 font-medium">PAUSED</span>
              )}
            </div>
            
            {/* Control Buttons */}
            <div className="flex items-center gap-2">
              {/* Shared PDF resources — available to both roles, any state */}
              <button
                data-testid="resources-btn"
                onClick={() => setShowResources(true)}
                className="text-sm px-4 py-2 rounded-full bg-[#7D202B] text-white hover:bg-[#963A42] transition-colors flex items-center gap-2"
              >
                <Paperclip size={16} />
                Resources
              </button>
              {/* Extend Button - Only for Care Seekers when session is active */}
              {!sessionEnded && user.role === 'care_seeker' && timerStarted && timeRemaining > 0 && !timerPaused && (
                <button
                  data-testid="extend-session-btn"
                  onClick={() => setShowExtensionPrompt(true)}
                  className="text-sm px-4 py-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Plus size={16} />
                  Extend
                </button>
              )}

              {/* Book Therapy - Only for Care Seekers when session is active */}
              {!sessionEnded && user.role === 'care_seeker' && timerStarted && timeRemaining > 0 && !timerPaused && (
                <button
                  data-testid="book-therapy-btn"
                  onClick={() => setShowBookingPrompt(true)}
                  className="text-sm px-4 py-2 rounded-full bg-primary text-white hover:opacity-90 transition-colors flex items-center gap-2"
                >
                  <CalendarPlus size={16} />
                  Book Therapy
                </button>
              )}
              
              {/* Pause/Resume Button - Only when session is active */}
              {!sessionEnded && timerStarted && timeRemaining > 0 && (
                <button
                  data-testid="pause-resume-btn"
                  onClick={timerPaused ? handleResumeTimer : handlePauseTimer}
                  className={`text-sm px-4 py-2 rounded-full transition-colors flex items-center gap-2 ${
                    timerPaused 
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                      : 'bg-amber-500 text-white hover:bg-amber-600'
                  }`}
                >
                  {timerPaused ? (
                    <>
                      <Play size={16} />
                      Resume
                    </>
                  ) : (
                    <>
                      <Pause size={16} />
                      Pause
                    </>
                  )}
                </button>
              )}
              
              {/* Summary Button - Provider Only */}
              {user.role === 'care_provider' && (
                <button
                  data-testid="view-summary-btn"
                  onClick={() => setShowSummary(true)}
                  className="text-sm px-4 py-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <FileText size={16} />
                  Summary
                </button>
              )}

              {/* Veyonika Session Note - Provider Only (write at end of session) */}
              {user.role === 'care_provider' && (
                <button
                  data-testid="veyonika-summary-btn"
                  onClick={() => setShowVeyonikaSummary(true)}
                  className="text-sm px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  <Sparkles size={16} />
                  Veyonika Summary
                </button>
              )}
              
              {/* End Session Button */}
              {!sessionEnded ? (
                <button
                  data-testid="end-session-btn"
                  onClick={endSession}
                  className="text-sm px-4 py-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  End Session
                </button>
              ) : (
                <span className="text-sm px-4 py-2 rounded-full bg-gray-400 text-white">
                  Session Ended
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Message Input Row */}
        <div className="border-t border-[#E2E8F0]">
          <div className="container mx-auto max-w-4xl px-6 py-3">
            {sessionEnded ? (
              <div className="text-center py-2 text-gray-500">
                <p>This session has ended. {user.role === 'care_provider' ? 'Please complete the session summary.' : 'Thank you.'}</p>
              </div>
            ) : (
              <>
                {replyTo && (
                  <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-[#FDF2F0] border-l-4 border-[#7D202B]" data-testid="reply-preview">
                    <CornerUpLeft size={16} className="text-[#7D202B] shrink-0" />
                    <div className="min-w-0 flex-1 text-xs text-[#7A625C]">
                      <div className="font-medium text-[#7D202B]">Replying to</div>
                      <div className="truncate">{replyTo.sender_role === 'system' ? 'Automated message' : replyTo.content}</div>
                    </div>
                    <button type="button" onClick={() => setReplyTo(null)} data-testid="reply-cancel" aria-label="Cancel reply" className="p-1 text-[#A0AEC0] hover:text-[#7D202B]">
                      <X size={16} />
                    </button>
                  </div>
                )}
                <form onSubmit={sendMessage} className="flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => setShowResources(true)}
                    data-testid="open-resources-btn"
                    aria-label="Shared resources"
                    title="Shared resources"
                    className="shrink-0 w-11 h-11 flex items-center justify-center rounded-xl border border-[#E2E8F0] text-[#7D202B] hover:bg-[#FDF2F0]"
                  >
                    <Paperclip size={20} />
                  </button>
                  <input
                    type="text"
                    data-testid="message-input"
                    value={newMessage}
                    disabled={guestLocked}
                    onChange={(e) => { setNewMessage(e.target.value); pingTyping(e.target.value.length > 0); }}
                    placeholder={guestLocked ? 'Sign in to continue the conversation…' : 'Type your message...'}
                    className="flex-1 px-4 py-3 rounded-xl border border-[#E2E8F0] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                  <button
                    type="submit"
                    data-testid="send-message-btn"
                    disabled={!newMessage.trim() || guestLocked}
                    className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={20} />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Extension Prompt Modal — wraps the live-priced SessionExtension component */}
      {showExtensionPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock size={28} />
                <div>
                  <h2 className="text-lg font-semibold">Need more time?</h2>
                  <p className="text-orange-100 text-xs">Extend in 15-minute bundles</p>
                </div>
              </div>
              <button
                onClick={() => setShowExtensionPrompt(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                data-testid="extension-modal-close"
              >
                <X size={22} />
              </button>
            </div>

            <div className="p-5">
              <SessionExtension
                sessionId={sessionId}
                session={session}
                user={user}
                onExtensionPaid={({ minutes }) => {
                  // Extend the local timer to match the paid extension
                  if (endTimeRef.current) {
                    endTimeRef.current = endTimeRef.current + minutes * 60 * 1000;
                  }
                  setExtendedMinutes((m) => m + minutes);
                  setTimeout(() => setShowExtensionPrompt(false), 1200);
                  fetchSession();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Provider: incoming in-session requests (extension / therapy booking) */}
      {!sessionEnded && user.role === 'care_provider' && (
        <div className="fixed bottom-4 right-4 z-[65] w-80 max-w-[90vw]">
          <InSessionRequests sessionId={sessionId} user={user} />
        </div>
      )}

      {/* Seeker: Therapy Booking Modal */}
      {showBookingPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-purple-600 p-5 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CalendarPlus size={28} />
                <div>
                  <h2 className="text-lg font-semibold">Book a Therapy Session</h2>
                  <p className="text-purple-100 text-xs">Your counsellor accepts, then you pay</p>
                </div>
              </div>
              <button
                onClick={() => setShowBookingPrompt(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                data-testid="booking-modal-close"
              >
                <X size={22} />
              </button>
            </div>
            <div className="p-5">
              <InSessionBooking
                sessionId={sessionId}
                user={user}
                defaultOpen={true}
                onBooked={() => setTimeout(() => setShowBookingPrompt(false), 1500)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Session Summary Modal (auto-opens after session ends) — original Session Summary */}
      <ChatSummary
        sessionId={sessionId}
        isOpen={showSummary}
        onClose={handleSummaryClose}
      />
      <VeyonikaSessionSummary
        sessionId={sessionId}
        isOpen={showVeyonikaSummary}
        onClose={() => setShowVeyonikaSummary(false)}
      />
      <SessionResources
        sessionId={sessionId}
        user={user}
        isOpen={showResources}
        onClose={() => setShowResources(false)}
      />

      {/* Phase 4: guest account-conversion (12-min free limit reached) */}
      <AccountConversionModal
        isOpen={isGuest && showConversion && !guestExpired}
        sessionId={sessionId}
        guestToken={localStorage.getItem('token')}
        nearlyDone={false}
      />

      {/* Phase 4: guest session expiry (3h max or 30-min inactivity) */}
      {isGuest && guestExpired && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" data-testid="guest-expired-overlay">
          <div className="bg-white max-w-md w-full rounded-3xl p-8 text-center">
            <h2 className="text-xl font-heading font-semibold text-[#3B2923] mb-2">Your private session has ended</h2>
            <p className="text-sm text-[#7A625C] leading-relaxed mb-6">
              For your privacy, this private session has ended. You can create an account to continue
              your support or start a new private chat.
            </p>
            <button onClick={() => navigate('/auth')} data-testid="guest-expired-signin" className="btn-primary w-full mb-3">Create account / Sign in</button>
            <button onClick={() => navigate('/guest-chat')} data-testid="guest-expired-restart" className="w-full py-3 rounded-full border border-[#E7D6D0] text-[#7D202B] font-medium">Start a new private chat</button>
          </div>
        </div>
      )}
    </div>
  );
}
